import React, { useContext, useEffect, useState } from "react";
import models from "../../Utils/SampleDataModels.json";
import { useFormContext } from "../createworkflow/FormContext";
import { ErrorBoundary } from "../common/ErrorBoundary";
import {
  ArrowBigDown,
  ArrowBigUp,
} from "lucide-react";
import MenuContext from "./MenuContext";
import { stageLayoutStyles, stageFormStyles, StageNavButtons } from "./helper";

export default function Stage6() {
  const { formData, updateFormData } = useFormContext();
  const [selectedModels, setSelectedModels] = useState([]);
  const [expandedModel, setExpandedModel] = useState(null);
  const { setActiveTab } = useContext(MenuContext);

  const handleSelect = (modelName) => {
    setSelectedModels((prev) =>
      prev.includes(modelName)
        ? prev.filter((name) => name !== modelName)
        : [...prev, modelName]
    );
  };

  const handleSelectAll = () => {
    const allModelNames = models.map((model) => model.name);
    const isAllSelected = allModelNames.every((name) => selectedModels.includes(name));

    setSelectedModels(isAllSelected ? [] : allModelNames);
  };

  const handleExpand = (modelName) =>
    setExpandedModel(expandedModel === modelName ? null : modelName);

  useEffect(() => {
    updateFormData("stage6", {
      ...formData.stage6,
      models: selectedModels,
    });
  }, [selectedModels]);

  useEffect(() => {
    const modelsFromForm = formData.stage6?.models;

    if (Array.isArray(modelsFromForm)) {
      setSelectedModels(modelsFromForm);
    } else if (typeof modelsFromForm === "string" && modelsFromForm.length > 0) {
      const parsed = modelsFromForm
        .replace(/[{}]/g, "")
        .split(",")
        .map((m) => m.trim())
        .filter(Boolean);
      setSelectedModels(parsed);
    }
  }, []);

  return (
    <ErrorBoundary>
      <div style={stageLayoutStyles.wrapper}>
        <div style={stageLayoutStyles.card}>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
            <p style={stageFormStyles.hint}>
              Select one or more models. <strong>{selectedModels.length}</strong> selected.
            </p>

            <button
              type="button"
              onClick={handleSelectAll}
              style={{
                ...stageFormStyles.chipBtn,
                background: selectedModels.length === models.length ? "#E06639" : "#032B4E",
                color: "#fff",
                borderColor: selectedModels.length === models.length ? "#E06639" : "#032B4E",
                minWidth: 140,
              }}
            >
              {selectedModels.length === models.length ? "Deselect All" : "Select All"}
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {models.map((model) => {
              const isSelected = selectedModels.includes(model.name);
              const isExpanded = expandedModel === model.name;
              return (
                <ModelCard
                  key={model.id}
                  model={model}
                  isSelected={isSelected}
                  isExpanded={isExpanded}
                  onSelect={() => handleSelect(model.name)}
                  onExpand={() => handleExpand(model.name)}
                />
              );
            })}
          </div>

          <StageNavButtons
            onBack={() => setActiveTab("Stage5")}
            onNext={() => setActiveTab("Stage7")}
          />
        </div>
      </div>
    </ErrorBoundary>
  );
}

const ModelCard = ({
  model,
  isSelected,
  isExpanded,
  onSelect,
  onExpand,
}) => (
  <div
    className="relative rounded-xl transition-all duration-200 overflow-hidden border"
    style={{
      borderColor: isSelected ? "#E06639" : "#e6eef4",
      background: isSelected ? "#fffbf3" : "#f6fafd",
      boxShadow: isSelected
        ? "0 4px 14px rgba(224, 102, 57, 0.12)"
        : "0 2px 8px rgba(12, 60, 84, 0.04)",
    }}
  >
    <div className="flex flex-col items-center p-4 text-center">
      <h3
        style={{
          ...stageFormStyles.sectionTitle,
          marginTop: 0,
          marginBottom: 6,
        }}
      >
        {model.name}
      </h3>

      <p
        style={{
          fontSize: 13,
          lineHeight: 1.45,
          color: "#6a7d8c",
          fontFamily: "Poppins, sans-serif",
          margin: 0,
        }}
      >
        {isExpanded ? model.description : `${model.description.slice(0, 130)}...`}
      </p>

      <div className="flex justify-center gap-3 mt-4">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onExpand();
          }}
          style={{
            ...stageFormStyles.chipBtn,
            background: "#fff",
            color: "#0c3c54",
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          {isExpanded ? (
            <>
              <ArrowBigUp className="w-4 h-4" />
              Show Less
            </>
          ) : (
            <>
              <ArrowBigDown className="w-4 h-4" />
              Show More
            </>
          )}
        </button>

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onSelect();
          }}
          style={{
            ...stageFormStyles.chipBtn,
            background: isSelected ? "#E06639" : "#032B4E",
            color: "#fff",
            borderColor: isSelected ? "#E06639" : "#032B4E",
          }}
        >
          {isSelected ? "Deselect" : "Select"}
        </button>
      </div>
    </div>
  </div>
);
